import React, {useEffect, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {useB2BReferralStats} from "../hooks/useB2BReferralStats.ts";
import {ReferredAgencyRow} from "../domain/entities/B2BReferralStats.ts";
import {getMarketOptions} from "./agenciesTable/marketOptions.ts";
import {initialsOf} from "../utils/initials.ts";
import IconSpinner from '../assets/icons/spinner.svg?react';
import IconDownload from '../assets/icons/download-white.svg?react';
import IconCopy from '../assets/icons/copy.svg?react';
import IconInfo from '../assets/icons/info.svg?react';
import IconCalendar from '../assets/icons/calendar.svg?react';

declare global {
    // eslint-disable-next-line @typescript-eslint/no-namespace
    namespace JSX {
        interface IntrinsicElements {
            'wistia-player': React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement> & {
                'media-id': string
                aspect?: string | number
            }
        }
    }
}

interface MyReferralsContainerProps {
    agencyId: string
}

const MONTH_NAMES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sept', 'oct', 'nov', 'dic']

const WISTIA_MEDIA_ID = 'zfumn2msax'
const WISTIA_SHARE_URL = 'https://51trips.wistia.com/s/zsfi19qu7kuhltw'
const WISTIA_DOWNLOAD_URL = `https://51trips.wistia.com/medias/${WISTIA_MEDIA_ID}/download`

function loadWistiaScripts() {
    if (document.querySelector('script[data-wistia="player"]')) return
    const playerScript = document.createElement('script')
    playerScript.src = 'https://fast.wistia.com/player.js'
    playerScript.async = true
    playerScript.dataset.wistia = 'player'
    document.head.appendChild(playerScript)

    const embedScript = document.createElement('script')
    embedScript.src = `https://fast.wistia.com/embed/${WISTIA_MEDIA_ID}.js`
    embedScript.async = true
    embedScript.type = 'module'
    embedScript.dataset.wistia = 'embed'
    document.head.appendChild(embedScript)
}

function formatMoney(amount: number, currency: string): string {
    // useGrouping must be forced 'always': es-ES's 'auto' default uses "min2"
    // grouping, which drops the thousands separator for 4-digit values (e.g. 7829).
    return new Intl.NumberFormat('es-ES', {style: 'currency', currency, maximumFractionDigits: 0, useGrouping: 'always'} as any).format(amount)
}

function formatMonthYear(monthStr: string): string {
    const [year, month] = monthStr.split('-').map(Number)
    if (!year || !month) return monthStr
    return `${MONTH_NAMES[month - 1]} ${year}`
}

export const MyReferralsContainer: React.FC<MyReferralsContainerProps> = ({agencyId}) => {
    const {t} = useTranslation('dashboard')
    const SHARE_LINKS = [
        {title: t('referrals.valuePropTitle'), url: 'https://51trips.com/es/for-businesses', Icon: IconInfo},
        {title: t('referrals.demoTitle'), url: 'https://51trips.com/es/book-a-demo', Icon: IconCalendar},
    ]
    const [year, setYear] = useState<number | undefined>(undefined)
    const [unit, setUnit] = useState<'cash' | 'tripbooks'>('cash')
    const [copiedKey, setCopiedKey] = useState<string | null>(null)
    const {stats, isLoadingStats} = useB2BReferralStats(agencyId, year, true)

    useEffect(() => {
        loadWistiaScripts()
    }, [])

    const activeYear = stats?.year
    const years = stats?.availableYears ?? []
    const unitPrice = stats?.cards.generatedVolume.unitPriceReference
    const canShowTripbooks = !!unitPrice && unitPrice > 0

    const monthly = useMemo(() => {
        const points = stats?.monthly ?? []
        const values = points.map((p) => (unit === 'tripbooks' && unitPrice ? p.cash / unitPrice : p.cash))
        const step = unit === 'tripbooks' ? 2 : 25
        const maxVal = Math.max(1, ...values)
        const ceiling = Math.max(step, Math.ceil(maxVal / step) * step)
        const ticks = [4, 3, 2, 1, 0].map((i) => {
            const v = (ceiling / 4) * i
            return unit === 'tripbooks' ? `${Math.round(v * 10) / 10}` : formatMoney(v, 'EUR')
        })
        const bars = points.map((p, i) => {
            const value = values[i] ?? 0
            return {
                key: p.month,
                label: MONTH_NAMES[i] ?? p.month,
                heightPx: Math.max(2, Math.round((value / ceiling) * 130)),
                title: unit === 'tripbooks' ? t('referrals.tripbooksSuffix', {n: Math.round(value * 10) / 10}) : formatMoney(p.cash, 'EUR'),
            }
        })
        return {ticks, bars}
    }, [stats?.monthly, unit, unitPrice, t])

    const copy = (key: string, text: string) => {
        navigator.clipboard?.writeText(text)
        setCopiedKey(key)
        setTimeout(() => setCopiedKey((k) => (k === key ? null : k)), 1500)
    }

    if (isLoadingStats || !stats) {
        return (
            <div className="flex items-center justify-center h-full">
                <IconSpinner className="size-8"/>
            </div>
        )
    }

    const {cards, referredAgencies} = stats

    return (
        <div className="h-full overflow-y-auto no-scrollbar">
            <header className="flex items-center gap-4 flex-wrap pb-5">
                <h1 className="text-[26px] font-semibold tracking-tight text-neutralBlack-900">{t('referrals.pageTitle')}</h1>
                <span className="flex-1"/>
                <div className="flex gap-2 flex-wrap">
                    {years.map((y) => (
                        <button
                            key={y}
                            type="button"
                            onClick={() => setYear(y)}
                            className={`h-[34px] inline-flex items-center px-4 rounded-full border text-[13.5px] font-medium transition-colors ${
                                activeYear === y
                                    ? 'bg-neutralBlack-900 border-neutralBlack-900 text-white'
                                    : 'bg-white border-neutralBlack-200 text-neutralBlack-700 hover:bg-neutralBlack-50'
                            }`}
                        >
                            {y}
                        </button>
                    ))}
                </div>
            </header>

            <div className="flex flex-col gap-3 max-w-[1160px] pb-10">

                <div className="grid gap-3" style={{gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))'}}>
                    <div className="min-w-0 bg-white border border-neutralBlack-200 rounded-2xl shadow-sm px-[22px] py-5 flex flex-col gap-3" style={{gridColumn: 'span 2'}}>
                        <span className="text-[11.5px] font-medium tracking-wide uppercase text-neutralBlack-500">{t('referrals.volumeGenerated')}</span>
                        <div className="flex items-stretch gap-8 flex-wrap">
                            <span className="flex-none flex flex-col gap-1">
                                <span className="text-[28px] font-semibold tracking-tight text-neutralBlack-900">{formatMoney(cards.generatedVolume.cash, cards.generatedVolume.currency)}</span>
                                <span className="text-xs text-neutralBlack-500">{t('referrals.inCash')}</span>
                            </span>
                            {canShowTripbooks && (
                                <>
                                    <span className="flex-none flex flex-col items-center justify-center gap-1.5 self-stretch">
                                        <span className="flex-1 w-px bg-neutralBlack-200"/>
                                        <span className="text-[11px] font-medium uppercase tracking-wide text-neutralBlack-400">{t('referrals.or')}</span>
                                        <span className="flex-1 w-px bg-neutralBlack-200"/>
                                    </span>
                                    <span className="flex-1 min-w-[110px] flex flex-col gap-1">
                                        <span className="text-[28px] font-semibold tracking-tight text-neutralBlack-900">{t('referrals.tripbooksSuffix', {n: cards.generatedVolume.tripbooks})}</span>
                                        <span className="text-xs text-neutralBlack-500">{t('referrals.towardsNextPack')}</span>
                                    </span>
                                </>
                            )}
                        </div>
                    </div>
                    <div className="min-w-0 bg-white border border-neutralBlack-200 rounded-2xl shadow-sm px-[22px] py-5 flex flex-col gap-2">
                        <span className="text-[11.5px] font-medium tracking-wide uppercase text-neutralBlack-500">{t('referrals.referrals')}</span>
                        <span className="text-[28px] font-semibold tracking-tight text-neutralBlack-900">{cards.referrals}</span>
                        <span className="text-[12.5px] text-neutralBlack-500">{t('referrals.agenciesThatContracted')}</span>
                    </div>
                    <div className="min-w-0 bg-white border border-neutralBlack-200 rounded-2xl shadow-sm px-[22px] py-5 flex flex-col gap-2">
                        <span className="text-[11.5px] font-medium tracking-wide uppercase text-neutralBlack-500">{t('referrals.commissionPct')}</span>
                        <span className="text-[28px] font-semibold tracking-tight text-neutralBlack-900">{Math.round(cards.commissionPct * 1000) / 10}%</span>
                        <span className="text-[12.5px] text-neutralBlack-500">{t('referrals.perContractedPack')}</span>
                    </div>
                </div>

                <section className="bg-white border border-neutralBlack-200 rounded-2xl shadow-sm px-6 py-[22px] flex flex-col gap-[18px]">
                    <div className="flex items-center gap-3.5 flex-wrap">
                        <span className="text-[15px] font-semibold tracking-tight text-neutralBlack-900">{t('referrals.volumeByMonth')}</span>
                        <span className="text-[13px] text-neutralBlack-500">{activeYear}</span>
                        <span className="flex-1"/>
                        {canShowTripbooks && (
                            <div className="flex gap-1.5 flex-wrap">
                                {(['cash', 'tripbooks'] as const).map((u) => (
                                    <button
                                        key={u}
                                        type="button"
                                        onClick={() => setUnit(u)}
                                        className={`h-8 inline-flex items-center px-3.5 rounded-full border text-[12.5px] whitespace-nowrap transition-colors ${
                                            unit === u
                                                ? 'bg-neutralBlack-900 border-neutralBlack-900 text-white'
                                                : 'bg-white border-neutralBlack-200 text-neutralBlack-700 hover:bg-neutralBlack-50'
                                        }`}
                                    >
                                        {u === 'cash' ? t('referrals.inEuros') : t('referrals.inTripbooks')}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                    <div className="flex gap-2.5" style={{height: 170}}>
                        <div className="w-14 flex-none flex flex-col justify-between items-end" style={{paddingBottom: 27}}>
                            {monthly.ticks.map((tick, i) => (
                                <span key={i} className="text-[10.5px] leading-none text-neutralBlack-400 whitespace-nowrap">{tick}</span>
                            ))}
                        </div>
                        <div className="flex-1 min-w-0 flex items-end gap-2.5 relative">
                            <div className="absolute inset-x-0 top-0 flex flex-col justify-between pointer-events-none" style={{bottom: 27}}>
                                {monthly.ticks.map((_, i) => (
                                    <span key={i} className="h-px bg-neutralBlack-100"/>
                                ))}
                            </div>
                            {monthly.bars.map((m) => (
                                <span key={m.key} className="relative z-10 flex-1 min-w-0 flex flex-col items-center gap-2">
                                    <span className="flex-1 w-full flex items-end justify-center">
                                        <span title={m.title} className="w-[70%] max-w-[26px] rounded-t-[5px] bg-[#04A3B3]" style={{height: `${m.heightPx}px`}}/>
                                    </span>
                                    <span className="text-[11px] text-neutralBlack-500">{m.label}</span>
                                </span>
                            ))}
                        </div>
                    </div>
                </section>

                <ReferralsTable rows={referredAgencies} total={cards.referrals} year={activeYear} totalCash={cards.generatedVolume.cash} currency={cards.generatedVolume.currency}/>

                <section className="flex flex-col gap-3 mt-5">
                    <div className="flex items-baseline gap-2.5 flex-wrap">
                        <h2 className="text-[17px] font-semibold tracking-tight text-neutralBlack-900">{t('referrals.shareMaterial')}</h2>
                        <span className="text-[13px] text-neutralBlack-500">{t('referrals.shareMaterialSubtitle')}</span>
                    </div>

                    <div className="bg-white border border-neutralBlack-200 rounded-2xl shadow-sm p-5 flex gap-[22px] flex-wrap">
                        <div className="flex-1 min-w-[280px] self-start rounded-2xl overflow-hidden bg-neutralBlack-100">
                            <wistia-player media-id={WISTIA_MEDIA_ID} aspect="1.7777777777777777"/>
                        </div>
                        <div className="flex-1 min-w-[230px] flex flex-col gap-3 justify-center">
                            <span className="flex flex-col gap-1">
                                <span className="text-[11.5px] font-medium tracking-wide uppercase text-neutralBlack-500">{t('referrals.presentationVideo')}</span>
                                <span className="text-[17px] font-semibold tracking-tight text-neutralBlack-900">{t('referrals.videoTitle')}</span>
                                <span className="text-[13px] text-neutralBlack-500">{t('referrals.videoDuration')}</span>
                            </span>
                            <div className="flex gap-2 flex-wrap">
                                <a
                                    href={WISTIA_DOWNLOAD_URL} target="_blank" rel="noreferrer"
                                    className="inline-flex items-center gap-2 h-10 px-4 rounded-full bg-neutralBlack-900 text-white text-[13.5px] font-medium hover:bg-neutralBlack-800 transition-colors"
                                >
                                    <IconDownload className="w-3.5 h-3.5"/>
                                    {t('referrals.downloadVideo')}
                                </a>
                                <button
                                    type="button"
                                    onClick={() => copy('video', WISTIA_SHARE_URL)}
                                    className="inline-flex items-center gap-2 h-10 px-4 rounded-full border border-neutralBlack-200 bg-white text-[13.5px] text-neutralBlack-700 hover:bg-neutralBlack-50 transition-colors"
                                >
                                    <IconCopy className="w-3.5 h-3.5"/>
                                    {copiedKey === 'video' ? t('referrals.linkCopied') : t('referrals.copyLink')}
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white border border-neutralBlack-200 rounded-2xl shadow-sm px-[22px] py-[18px] flex flex-col gap-3">
                        <span className="text-[11.5px] font-medium tracking-wide uppercase text-neutralBlack-500">{t('referrals.links')}</span>
                        {SHARE_LINKS.map((l) => (
                            <div key={l.url} className="flex items-center gap-3.5 py-3 border-t border-neutralBlack-100 flex-wrap">
                                <span className="w-8 h-8 flex-none rounded-[10px] bg-[#E4F4F6] flex items-center justify-center text-[#04A3B3]">
                                    <l.Icon className="w-[15px] h-[15px]"/>
                                </span>
                                <span className="flex-1 min-w-[180px] flex flex-col gap-0.5">
                                    <span className="text-[13.5px] font-medium text-neutralBlack-900">{l.title}</span>
                                    <a href={l.url} target="_blank" rel="noreferrer" className="text-[12.5px] whitespace-nowrap overflow-hidden text-ellipsis block">
                                        {l.url.replace('https://', '')}
                                    </a>
                                </span>
                                <button
                                    type="button"
                                    onClick={() => copy(l.url, l.url)}
                                    className="inline-flex items-center gap-1.5 h-[34px] px-3.5 rounded-full border border-neutralBlack-200 bg-white text-[12.5px] text-neutralBlack-700 hover:bg-neutralBlack-50 whitespace-nowrap transition-colors"
                                >
                                    <IconCopy className="w-3 h-3"/>
                                    {copiedKey === l.url ? t('referrals.copied') : t('referrals.copy')}
                                </button>
                            </div>
                        ))}
                    </div>
                </section>
            </div>
        </div>
    )
}

const ReferralsTable: React.FC<{
    rows: ReferredAgencyRow[]
    total: number
    year?: number
    totalCash: number
    currency: string
}> = ({rows, total, year, totalCash, currency}) => {
    const {t} = useTranslation('dashboard')
    const COLUMNS = [
        t('referrals.columnAgency'), t('referrals.columnContactPerson'), t('referrals.columnMarket'), t('referrals.columnPackage'),
        t('referrals.columnPages'), t('referrals.columnPrice'), t('referrals.columnContractedAt'), t('referrals.columnCommission'),
    ]
    const MARKET_BY_VALUE = useMemo(
        () => Object.fromEntries(getMarketOptions().map((m) => [m.value, m])),
        []
    )
    return (
    <section className="bg-white border border-neutralBlack-200 rounded-2xl shadow-sm overflow-hidden">
        <div className="flex items-center gap-3 flex-wrap px-6 pt-5 pb-3.5">
            <span className="text-[15px] font-semibold tracking-tight text-neutralBlack-900">{t('referrals.recommendedAgencies')}</span>
            <span className="text-[13px] text-neutralBlack-500">{t('referrals.totalInYear', {total, year})}</span>
        </div>
        <div className="overflow-x-auto">
            <table className="w-full border-collapse text-[13.5px]" style={{minWidth: 1000}}>
                <thead>
                <tr>
                    {COLUMNS.map((h, i) => (
                        <th
                            key={h}
                            className={`${i === 0 ? 'text-left px-6' : i === 7 ? 'text-right px-6' : i >= 4 && i <= 6 ? 'text-center px-3.5' : 'text-left px-3.5'} py-2.5 text-[11px] font-medium tracking-wide uppercase text-neutralBlack-400 border-t border-b border-neutralBlack-200`}
                        >
                            {h}
                        </th>
                    ))}
                </tr>
                </thead>
                <tbody>
                {rows.length === 0 ? (
                    <tr>
                        <td colSpan={8} className="py-10 px-6 text-center text-[13.5px] text-neutralBlack-500">
                            {t('referrals.noneInYear', {year})}
                        </td>
                    </tr>
                ) : rows.map((r) => {
                    const market = r.market ? MARKET_BY_VALUE[r.market] : undefined
                    return (
                        <tr key={r.agencyId} className="border-b border-neutralBlack-200 last:border-0 hover:bg-neutralBlack-50">
                            <td className="px-6 py-3 whitespace-nowrap">
                                <span className="flex items-center gap-2.5">
                                    <span className="w-[30px] h-[30px] flex-none rounded-[9px] bg-neutralBlack-100 border border-neutralBlack-200 flex items-center justify-center text-[10.5px] font-semibold text-neutralBlack-700">
                                        {initialsOf(r.name)}
                                    </span>
                                    <span className="text-[13.5px] font-medium text-neutralBlack-900">{r.name}</span>
                                </span>
                            </td>
                            <td className="px-3.5 py-3 whitespace-nowrap">
                                <span className="flex flex-col gap-0.5">
                                    <span className="text-neutralBlack-900">{r.contactName ?? '—'}</span>
                                    <span className="text-xs text-neutralBlack-500">{r.contactEmail ?? ''}</span>
                                </span>
                            </td>
                            <td className="px-3.5 py-3 whitespace-nowrap">
                                <span className="flex items-center gap-2 text-neutralBlack-700">
                                    {market?.flag && <img src={market.flag} alt={market.label} className="w-[18px] h-[13px] rounded-sm object-cover shadow-[0_0_0_1px_rgba(0,0,0,.08)]"/>}
                                    {market?.label ?? r.market ?? '—'}
                                </span>
                            </td>
                            <td className="px-3.5 py-3 whitespace-nowrap text-neutralBlack-700">{r.packLabel}</td>
                            <td className="px-3.5 py-3 text-center whitespace-nowrap text-neutralBlack-700">{r.pages ?? '—'}</td>
                            <td className="px-3.5 py-3 text-center whitespace-nowrap text-neutralBlack-700">{formatMoney(r.price, currency)}</td>
                            <td className="px-3.5 py-3 text-center whitespace-nowrap text-neutralBlack-700">{formatMonthYear(r.contractedAt)}</td>
                            <td className="px-6 py-3 text-right whitespace-nowrap font-medium text-neutralBlack-900">{formatMoney(r.commission, currency)}</td>
                        </tr>
                    )
                })}
                </tbody>
                {rows.length > 0 && (
                    <tfoot>
                    <tr>
                        <td colSpan={7} className="px-6 py-3.5 text-[13px] text-neutralBlack-500">{t('referrals.totalInYearFooter', {year})}</td>
                        <td className="px-6 py-3.5 text-right text-[14px] font-semibold text-neutralBlack-900">{formatMoney(totalCash, currency)}</td>
                    </tr>
                    </tfoot>
                )}
            </table>
        </div>
    </section>
    )
}
